import { Event } from "../../../types";
import { toDateFormat } from "@/lib/dateHelpers";
import Table, { TableHeading } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { DateTime } from "luxon";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";

interface SessionsTableProps {
  event: Event;
}

interface SessionTableData {
  id: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  duration: string;
  maxAttendance: string;
}

export function SessionsTable({ event }: SessionsTableProps) {
  const getDayOfWeek = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { weekday: "long" });
  };

  const getDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();

    const totalHours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    // If duration is 24 hours or more, show in days and hours
    if (totalHours >= 24) {
      const days = Math.floor(totalHours / 24);
      const remainingHours = totalHours % 24;

      if (remainingHours === 0) {
        return `${days} day${days !== 1 ? "s" : ""}`;
      }
      return `${days} day${days !== 1 ? "s" : ""}, ${remainingHours} hr${remainingHours !== 1 ? "s" : ""}`;
    }

    // For less than 24 hours, use the original logic
    if (minutes === 0) {
      return `${totalHours} hr${totalHours !== 1 ? "s" : ""}`;
    }
    return `${totalHours} hr${totalHours !== 1 ? "s" : ""} ${minutes} min`;
  };

  const getTimeWithTimezone = (dateString: string, timezone: string) => {
    const dateTime = DateTime.fromISO(dateString).setZone(timezone);
    if (!dateTime.isValid) {
      return "";
    }
    const time = dateTime.toFormat("h:mm a");
    const tzAbbr = dateTime.toFormat("ZZZZ");
    return `${time} (${tzAbbr})`;
  };

  const tableData: SessionTableData[] = (event.sessions || []).map((session) => ({
    id: session.id,
    startDate: toDateFormat(session.startsAt, { tz: event.timezone }) || "",
    endDate: toDateFormat(session.endsAt, { tz: event.timezone }) || "",
    startTime: getTimeWithTimezone(session.startsAt, event.timezone),
    endTime: getTimeWithTimezone(session.endsAt, event.timezone),
    dayOfWeek: getDayOfWeek(session.startsAt),
    duration: getDuration(session.startsAt, session.endsAt),
    maxAttendance: session.maxAttendance?.toString() || "--"
  }));

  const columns = useMemo<ColumnDef<SessionTableData, unknown>[]>(
    () => [
      {
        accessorKey: "startDate",
        header: "Start Date",
        id: "startDate",
        enableSorting: false
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        id: "endDate",
        enableSorting: false
      },
      {
        accessorKey: "startTime",
        header: "Start Time",
        id: "startTime",
        enableSorting: false
      },
      {
        accessorKey: "endTime",
        header: "End Time",
        id: "endTime",
        enableSorting: false
      },
      {
        accessorKey: "dayOfWeek",
        header: "Day of the Week",
        id: "dayOfWeek",
        enableSorting: false
      },
      {
        accessorKey: "duration",
        header: "Duration",
        id: "duration",
        enableSorting: false
      },
      {
        accessorKey: "maxAttendance",
        header: "Max Attendance",
        id: "maxAttendance",
        enableSorting: false
      }
    ],
    []
  );

  return (
    <>
      <TableHeading heading="Sessions" body="Individual sessions included in this event" />
      <Table
        data={tableData}
        loading={false}
        columns={columns}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Sessions" caption="This event has no sessions configured" iconName="event" />
        )}
        data-testid="sessions-table"
      />
    </>
  );
}
